import { BILL_TYPE, ITEM_TYPE, PRODUCT_TYPE } from '@/utils/enum'
import { isStackedDiscountLine } from '@/utils/voucherDiscountStack'

export function parseImeiCodes(imeiStr) {
  if (!imeiStr || !String(imeiStr).trim()) return []
  return String(imeiStr)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** Chuỗi hiển thị "imei1, imei2"; cắt + ... khi dài; tooltip dùng `full` khi `truncated`. */
export function imeiListShortLabel(imeiStr, maxLen = 48) {
  const codes = parseImeiCodes(imeiStr)
  const full = codes.join(', ')
  if (full.length <= maxLen) {
    return { full, short: full, truncated: false }
  }
  let cut = full.slice(0, Math.max(0, maxLen - 3)).trimEnd()
  const lastComma = cut.lastIndexOf(', ')
  if (lastComma >= Math.min(16, maxLen * 0.35)) {
    cut = cut.slice(0, lastComma).trimEnd()
  }
  return { full, short: `${cut}...`, truncated: true }
}

/**
 * Chia chiết khấu tổng thành n dòng (tổng khớp, phần dư rải đều dòng đầu).
 */
export function splitDiscountAcrossLines(totalDiscount, lineCount) {
  const n = Math.max(0, Math.floor(Number(lineCount) || 0))
  if (n <= 0) return []
  const t = Math.round(Number(totalDiscount) || 0)
  const base = Math.floor(t / n)
  let rem = t - base * n
  return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0))
}

function withImeiDisplayFields(item) {
  const imei = item.imeiCode || item.IMEI || ''
  return {
    ...item,
    IMEI: imei,
    imeiCode: imei,
  }
}

function mergeWholesaleImeiRun(run) {
  const first = run[0]
  const codes = run.map((r) => String(r.imeiCode || '').trim()).filter(Boolean)
  const joined = codes.join(',')
  const qty = run.reduce((s, r) => s + (Number(r.quantity) || 1), 0)
  const discountAmount = run.reduce((s, r) => s + (Number(r.discountAmount) || 0), 0)
  const price = Number(first.sellingPriceMask || first.sellingPrice) || 0
  const detailIds = run.map((r) => r.id)

  return {
    ...first,
    id: first.id,
    _wholesaleMergedDetailIds: detailIds,
    imeiCode: joined,
    IMEI: joined,
    quantity: qty,
    discountAmount,
    totalPrice: qty * price - discountAmount,
  }
}

function productGroupKey(productId) {
  return productId == null ? '' : String(productId)
}

/**
 * Gom mọi dòng MAIN + PRODUCT_IMEI cùng productId thành một dòng hiển thị (bán buôn),
 * kể cả khi xen quà/kèm giữa các dòng MAIN (giống màn thêm mới: một dòng nhiều IMEI).
 * Quà/kèm có belongBillDetailId trỏ tới bất kỳ MAIN nào trong nhóm → về id MAIN đầu nhóm.
 */
export function mergeWholesaleImeiBillRowsForDisplay(rows, billType) {
  if (billType !== BILL_TYPE.WHOLESALE_INVOICE || !rows?.length) return rows

  const imeiMainByProductId = new Map()
  for (const row of rows) {
    if (
      row.itemType === ITEM_TYPE.MAIN &&
      row.productType === PRODUCT_TYPE.PRODUCT_IMEI
    ) {
      const key = productGroupKey(row.productId)
      if (!imeiMainByProductId.has(key)) {
        imeiMainByProductId.set(key, [])
      }
      imeiMainByProductId.get(key).push(row)
    }
  }

  const redirectMap = new Map()
  imeiMainByProductId.forEach((group) => {
    if (group.length <= 1) return
    const primaryId = group[0].id
    group.forEach((r) => {
      if (r.id != null && primaryId != null) {
        redirectMap.set(String(r.id), String(primaryId))
      }
    })
  })

  const applyBelongRedirect = (r) => {
    const copy = { ...r }
    const bid = copy.belongBillDetailId
    if (bid != null && redirectMap.has(String(bid))) {
      copy.belongBillDetailId = redirectMap.get(String(bid))
    }
    return copy
  }

  const emittedImeiMainKey = new Set()
  const out = []

  for (const row of rows) {
    const isMainImei =
      row.itemType === ITEM_TYPE.MAIN && row.productType === PRODUCT_TYPE.PRODUCT_IMEI

    if (!isMainImei) {
      out.push(applyBelongRedirect(row))
      continue
    }

    const key = productGroupKey(row.productId)
    if (emittedImeiMainKey.has(key)) {
      continue
    }
    emittedImeiMainKey.add(key)

    const group = imeiMainByProductId.get(key) || [row]
    if (group.length === 1) {
      out.push(withImeiDisplayFields(applyBelongRedirect(group[0])))
    } else {
      out.push(mergeWholesaleImeiRun(group))
    }
  }

  return out
}

function listBillItemRowItemType(row) {
  if (row.itemType != null) return row.itemType
  return row.billItemType
}

/**
 * Dòng được gom trên API danh sách v1/bills.
 * List trả về: (productType=1, typeProduct=2) = dòng sản phẩm IMEI chính;
 * (productType=2, typeProduct=1) = quà — trùng số 2 với PRODUCT_IMEI trong enum
 * nên không được dùng một mình productType === 2 để nhận diện IMEI.
 */
function isWholesaleListMainImeiRow(row) {
  const imei = String(row.imeiCode || row.IMEI || '').trim()
  if (!imei) return false

  const it = listBillItemRowItemType(row)
  if (it === ITEM_TYPE.GIFT || it === ITEM_TYPE.ATTACH) return false

  const pt = Number(row.productType)
  const tp = Number(row.typeProduct)

  if (pt === PRODUCT_TYPE.PRODUCT && tp === PRODUCT_TYPE.PRODUCT_IMEI) {
    return it == null || it === ITEM_TYPE.MAIN
  }

  if (it === ITEM_TYPE.MAIN && pt === PRODUCT_TYPE.PRODUCT_IMEI) {
    return true
  }

  return false
}

function mergeWholesaleListProductGroup(group) {
  const first = withImeiDisplayFields(group[0])
  const codeList = []
  for (const r of group) {
    codeList.push(...parseImeiCodes(r.imeiCode || r.IMEI))
  }
  const uniqueCodes = [...new Set(codeList)]
  const qty = group.reduce((s, r) => s + (Number(r.quantity) || 0), 0)
  const discountItemAmountSum = group.reduce(
    (s, r) => s + (Number(r.discountItemAmount) || 0),
    0,
  )

  let discount = first.discount
  if (first.discountType === 1) {
    discount = group.reduce((s, r) => s + (Number(r.discount) || 0), 0)
  }

  return {
    ...first,
    id: `wg-list-${first.productId}-${first.id}`,
    quantity: qty,
    imeiCode: uniqueCodes.join(', '),
    IMEI: uniqueCodes.join(', '),
    discount,
    discountItemAmount: discountItemAmountSum,
  }
}

/**
 * Danh sách hóa đơn (v1/bills): gom các dòng MAIN + PRODUCT_IMEI cùng productId
 * thành một dòng — SL tổng, nhiều IMEI — giống màn tạo/sửa hóa đơn bán buôn.
 */
export function groupWholesaleBillListProducts(listProduct, billType) {
  if (billType !== BILL_TYPE.WHOLESALE_INVOICE || !listProduct?.length) {
    return listProduct
  }

  const byPid = new Map()
  for (const row of listProduct) {
    if (!isWholesaleListMainImeiRow(row)) continue
    const key = productGroupKey(row.productId)
    if (!byPid.has(key)) byPid.set(key, [])
    byPid.get(key).push(row)
  }

  const emitted = new Set()
  const out = []

  for (const row of listProduct) {
    if (!isWholesaleListMainImeiRow(row)) {
      out.push(row)
      continue
    }
    const key = productGroupKey(row.productId)
    if (emitted.has(key)) continue
    emitted.add(key)

    const group = byPid.get(key) || [row]
    if (group.length === 1) {
      out.push(withImeiDisplayFields({ ...row }))
    } else {
      out.push(mergeWholesaleListProductGroup(group))
    }
  }
  return out
}

/**
 * Một dòng UI (nhiều IMEI) -> n dòng payload API, mỗi IMEI một billItem.
 * @returns {Array|null} null nếu không cần tách
 */
export function expandWholesaleImeiMainToPayloadRows(billItem, gifts, attachs, billType) {
  if (billType !== BILL_TYPE.WHOLESALE_INVOICE) return null
  if (billItem.productType !== PRODUCT_TYPE.PRODUCT_IMEI) return null

  const codes = parseImeiCodes(billItem.imeiCode || billItem.IMEI)
  if (codes.length <= 1) return null

  const n = codes.length
  const discountParts = splitDiscountAcrossLines(billItem.discountAmount || 0, n)
  const stacked = isStackedDiscountLine(billItem)
  const progParts = stacked
    ? splitDiscountAcrossLines(Number(billItem.discountProgramAmount || 0), n)
    : null
  const vouchParts = stacked
    ? splitDiscountAcrossLines(Number(billItem.voucherAmount || 0), n)
    : null
  const detailIds = billItem._wholesaleMergedDetailIds || []
  const price = billItem.sellingPriceMask || billItem.sellingPrice || 0

  return codes.map((code, i) => {
    const row = {
      id: detailIds[i] != null ? detailIds[i] : i === 0 ? billItem.id || null : null,
      productId: billItem.productId,
      productPrice: price,
      quantity: 1,
      discountAmount: discountParts[i] || 0,
      imeiCode: code,
      billItemType: billItem.itemType,
      accessoryGroupId: billItem.accessoryGroupId,
      accessoryGroupCode: billItem.accessoryGroupCode,
      repurchasePrice: 0,
      isGiftTaken: billItem.checked,
      note: i === 0 ? billItem.note : '',
      gifts: i === 0 ? gifts : [],
      attachs: i === 0 ? attachs : [],
      discountType: billItem.discountType,
      discountProgramId: billItem.discountProgramId,
      issuedVat: billItem.isGetVat,
      merchantId: billItem.merchantId || 1,
      voucherId: i === 0 ? billItem.voucherId || null : null,
      voucherDetailId: i === 0 ? billItem.voucherDetailId || null : null,
      voucherCode: i === 0 ? billItem.voucherCode || null : null,
    }
    if (stacked && progParts && vouchParts) {
      row.discountProgramAmount = progParts[i] || 0
      row.voucherAmount = vouchParts[i] || 0
    }
    return row
  })
}
